// ============================================================================
//  HR Module — Composants salaire d'un contrat
//  Page : /hr/contracts/:id/components
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft, Plus, Trash2, Edit2, X, Info,
  Briefcase, Calculator,
} from 'lucide-react'
import {
  hrApi,
  type ContractSalaryComponent,
  type CreateContractComponentDto,
  type SalaryComponentType,
} from '@/modules/hr/api/hr.api'
import { useNotificationStore } from '@/stores/notification.store'
import { fmtXOF } from '@/lib/decimal'

// ─── Préselections par type ──────────────────────────────────────────────────
const COMPONENT_PRESETS: Record<SalaryComponentType, {
  code: string; label: string; imposable: boolean; ipres: boolean; css: boolean; cfce: boolean; prorata: boolean
}> = {
  BASE_SALARY:           { code: 'BASE',     label: 'Salaire de base',                imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: true  },
  SURSALARY:             { code: 'SURSAL',   label: 'Sursalaire',                      imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: true  },
  TRANSPORT_ALLOWANCE:   { code: 'TRANSP',   label: 'Indemnité de transport',          imposable: false, ipres: false, css: false, cfce: false, prorata: false },
  HOUSING_ALLOWANCE:     { code: 'LOGT',     label: 'Indemnité de logement',           imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: false },
  SENIORITY_BONUS:       { code: 'ANCIEN',   label: 'Prime d\'ancienneté',             imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: true  },
  PERFORMANCE_BONUS:     { code: 'PRIME_PF', label: 'Prime de performance',            imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: false },
  PRESENCE_BONUS:        { code: 'PRESENCE', label: 'Prime de présence',               imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: true  },
  FUNCTION_ALLOWANCE:    { code: 'FONCT',    label: 'Indemnité de fonction',           imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: false },
  PHONE_ALLOWANCE:       { code: 'TEL',      label: 'Indemnité téléphone',             imposable: false, ipres: false, css: false, cfce: false, prorata: false },
  INTERNET_ALLOWANCE:    { code: 'INET',     label: 'Indemnité internet',              imposable: false, ipres: false, css: false, cfce: false, prorata: false },
  IN_KIND_BENEFIT:       { code: 'AVNAT',    label: 'Avantage en nature',              imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: false },
  OTHER_FIXED_INCOME:    { code: 'AUTRE',    label: 'Autre élément fixe',              imposable: true,  ipres: true,  css: true,  cfce: true,  prorata: false },
}

const TYPE_LABELS: Record<SalaryComponentType, string> = {
  BASE_SALARY:           'Salaire de base',
  SURSALARY:             'Sursalaire',
  TRANSPORT_ALLOWANCE:   'Transport',
  HOUSING_ALLOWANCE:     'Logement',
  SENIORITY_BONUS:       'Ancienneté',
  PERFORMANCE_BONUS:     'Prime performance',
  PRESENCE_BONUS:        'Prime présence',
  FUNCTION_ALLOWANCE:    'Indemnité fonction',
  PHONE_ALLOWANCE:       'Téléphone',
  INTERNET_ALLOWANCE:    'Internet',
  IN_KIND_BENEFIT:       'Avantage en nature',
  OTHER_FIXED_INCOME:    'Autre élément fixe',
}

export default function ContractComponentsPage() {
  const { id }    = useParams<{ id: string }>()  // contractId
  const navigate  = useNavigate()
  const qc        = useQueryClient()
  const notify    = useNotificationStore(s => s.addNotification)

  const [showForm, setShowForm] = useState(false)
  const [editing,  setEditing]  = useState<ContractSalaryComponent | null>(null)

  const { data: contract, isLoading: loadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn:  () => hrApi.contracts.get(id!),
    enabled:  !!id,
  })

  const { data: components = [], isLoading: loadingComponents } = useQuery({
    queryKey: ['contract-components', id],
    queryFn:  () => hrApi.payroll.listComponents(id!),
    enabled:  !!id,
  })

  const deleteMut = useMutation({
    mutationFn: (compId: string) => hrApi.payroll.deleteComponent(compId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-components', id] })
      qc.invalidateQueries({ queryKey: ['contract', id] })
      notify({ type: 'success', message: 'Composant supprimé' })
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur' }),
  })

  if (loadingContract || loadingComponents) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-8 h-8 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!contract) {
    return <div className="p-6 text-[var(--tx3)]">Contrat introuvable.</div>
  }

  const totalActive = components.reduce((sum, c) => sum + Number(c.amount), 0)
  const baseImpo    = components.filter(c => c.isImposable).reduce((sum, c) => sum + Number(c.amount), 0)

  return (
    <div className="space-y-5 nx-fade-up">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--brand-dark)]">
              Composants salaire — {contract.employee?.firstName} {contract.employee?.lastName}
            </h1>
            <p className="text-xs text-[var(--tx3)] mt-0.5">
              {contract.type} · {contract.isCadre ? 'Cadre' : 'Non-cadre'}
              {contract.category ? ` · ${contract.category}` : ''}
            </p>
          </div>
        </div>

        <button
          onClick={() => { setEditing(null); setShowForm(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Ajouter un composant
        </button>
      </div>

      {/* SOMMAIRE */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-[var(--bd)] rounded-xl p-4">
          <p className="text-xs text-[var(--tx3)]">Total brut configuré</p>
          <p className="text-xl font-bold text-[var(--brand-dark)] mt-1">{fmtXOF(totalActive)}</p>
          <p className="text-xs text-[var(--tx3)] mt-1">{components.length} composant(s)</p>
        </div>
        <div className="bg-white border border-[var(--bd)] rounded-xl p-4">
          <p className="text-xs text-[var(--tx3)]">Base imposable</p>
          <p className="text-xl font-bold text-[#7C3AED] mt-1">{fmtXOF(baseImpo)}</p>
          <p className="text-xs text-[var(--tx3)] mt-1">soumis IRPP + cotisations</p>
        </div>
        <div className="bg-white border border-[var(--bd)] rounded-xl p-4">
          <p className="text-xs text-[var(--tx3)]">Non imposable</p>
          <p className="text-xl font-bold text-[#059669] mt-1">{fmtXOF(totalActive - baseImpo)}</p>
          <p className="text-xs text-[var(--tx3)] mt-1">transport, frais professionnels</p>
        </div>
      </div>

      {/* INFO BOX */}
      <div className="bg-[#EFF6FF] border border-[var(--blue)]/30 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-[var(--blue-d)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--blue-d)]">
          Les composants définissent la <strong>structure récurrente</strong> du salaire. Ils sont automatiquement
          appliqués lors de la création des bulletins. Pour les éléments ponctuels (heures sup, primes occasionnelles, retenues),
          utilise la section <strong>Variables paie</strong> sur le bulletin individuel.
        </div>
      </div>

      {/* TABLEAU */}
      <div className="bg-white border border-[var(--bd)] rounded-xl overflow-hidden">
        {components.length === 0 ? (
          <div className="p-8 text-center">
            <Briefcase className="w-10 h-10 text-[var(--tx3)] mx-auto mb-2" />
            <p className="text-sm text-[var(--tx3)]">Aucun composant configuré.</p>
            <p className="text-xs text-[var(--tx3)] mt-1">Ajoute au moins un BASE_SALARY pour pouvoir calculer les bulletins.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)] bg-[var(--bg)] text-xs text-[var(--tx3)] uppercase tracking-wider">
                <th className="text-left py-3 px-4 font-medium">Type</th>
                <th className="text-left py-3 px-4 font-medium">Code</th>
                <th className="text-left py-3 px-4 font-medium">Libellé</th>
                <th className="text-right py-3 px-4 font-medium">Montant</th>
                <th className="text-center py-3 px-4 font-medium">Imposable</th>
                <th className="text-center py-3 px-4 font-medium">IPRES</th>
                <th className="text-center py-3 px-4 font-medium">CSS</th>
                <th className="text-center py-3 px-4 font-medium">Prorata</th>
                <th className="text-right py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {components.map(c => (
                <tr key={c.id} className="hover:bg-[var(--bg)]">
                  <td className="py-2.5 px-4">
                    <span className="text-xs px-2 py-0.5 bg-[var(--surface-2)] text-[var(--tx2)] rounded font-medium">
                      {TYPE_LABELS[c.type] ?? c.type}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-xs font-mono text-[var(--tx3)]">{c.code}</td>
                  <td className="py-2.5 px-4 text-[var(--tx2)]">{c.label}</td>
                  <td className="py-2.5 px-4 text-right font-semibold text-[var(--brand-dark)]">{fmtXOF(c.amount)}</td>
                  <td className="py-2.5 px-4 text-center">
                    {c.isImposable ? <span className="text-[#059669]">●</span> : <span className="text-[var(--tx3)]">○</span>}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    {c.isSubjectToIpres ? <span className="text-[#059669]">●</span> : <span className="text-[var(--tx3)]">○</span>}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    {c.isSubjectToCss ? <span className="text-[#059669]">●</span> : <span className="text-[var(--tx3)]">○</span>}
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    {c.subjectToProrata ? <span className="text-[#059669]">●</span> : <span className="text-[var(--tx3)]">○</span>}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditing(c); setShowForm(true) }}
                        className="p-1.5 hover:bg-[#EFF6FF] rounded transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-3.5 h-3.5 text-[var(--blue-d)]" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Supprimer le composant "${c.label}" ?`)) deleteMut.mutate(c.id)
                        }}
                        className="p-1.5 hover:bg-[#FEF2F2] rounded transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-[#DC2626]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[var(--bg)] border-t-2 border-[var(--bd)]">
                <td colSpan={3} className="py-3 px-4 text-xs uppercase tracking-wider text-[var(--tx3)] font-bold">
                  Total
                </td>
                <td className="py-3 px-4 text-right font-bold text-[var(--brand-dark)]">{fmtXOF(totalActive)}</td>
                <td colSpan={5}></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* MODAL — Création/édition */}
      {showForm && id && (
        <ComponentFormModal
          contractId={id}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

// ============================================================================
//  MODAL — Création/édition d'un composant
// ============================================================================

function ComponentFormModal({
  contractId, editing, onClose,
}: {
  contractId: string
  editing:    ContractSalaryComponent | null
  onClose:    () => void
}) {
  const qc     = useQueryClient()
  const notify = useNotificationStore(s => s.addNotification)
  const isEdit = !!editing

  const [type,             setType]             = useState<SalaryComponentType>(editing?.type ?? 'BASE_SALARY')
  const [code,             setCode]             = useState(editing?.code ?? COMPONENT_PRESETS.BASE_SALARY.code)
  const [label,            setLabel]            = useState(editing?.label ?? COMPONENT_PRESETS.BASE_SALARY.label)
  const [amount,           setAmount]           = useState(editing ? String(editing.amount) : '')
  const [isImposable,      setIsImposable]      = useState(editing?.isImposable      ?? true)
  const [isSubjectToIpres, setIsSubjectToIpres] = useState(editing?.isSubjectToIpres ?? true)
  const [isSubjectToCss,   setIsSubjectToCss]   = useState(editing?.isSubjectToCss   ?? true)
  const [isSubjectToCfce,  setIsSubjectToCfce]  = useState(editing?.isSubjectToCfce  ?? true)
  const [subjectToProrata, setSubjectToProrata] = useState(editing?.subjectToProrata ?? true)
  const [order,            setOrder]            = useState(editing ? String(editing.order) : '0')
  const [notes,            setNotes]            = useState(editing?.notes ?? '')

  const handleTypeChange = (newType: SalaryComponentType) => {
    setType(newType)
    if (!isEdit) {  // ne pas écraser si en édition
      const preset = COMPONENT_PRESETS[newType]
      setCode(preset.code)
      setLabel(preset.label)
      setIsImposable(preset.imposable)
      setIsSubjectToIpres(preset.ipres)
      setIsSubjectToCss(preset.css)
      setIsSubjectToCfce(preset.cfce)
      setSubjectToProrata(preset.prorata)
    }
  }

  const createMut = useMutation({
    mutationFn: (dto: CreateContractComponentDto) => hrApi.payroll.createComponent(contractId, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-components', contractId] })
      qc.invalidateQueries({ queryKey: ['contract', contractId] })
      notify({ type: 'success', message: 'Composant ajouté' })
      onClose()
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur' }),
  })

  const updateMut = useMutation({
    mutationFn: (dto: Partial<CreateContractComponentDto>) =>
      hrApi.payroll.updateComponent(editing!.id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contract-components', contractId] })
      qc.invalidateQueries({ queryKey: ['contract', contractId] })
      notify({ type: 'success', message: 'Composant mis à jour' })
      onClose()
    },
    onError: (err: any) => notify({ type: 'error', message: err?.response?.data?.message || 'Erreur' }),
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!code || !label || !amount || Number(amount) <= 0) {
      notify({ type: 'error', message: 'Code, libellé et montant > 0 requis' })
      return
    }
    const dto = {
      type,
      code,
      label,
      amount: Number(amount),
      isImposable,
      isSubjectToIpres,
      isSubjectToCss,
      isSubjectToCfce,
      subjectToProrata,
      order: parseInt(order) || 0,
      notes: notes || undefined,
    }
    if (isEdit) updateMut.mutate(dto)
    else        createMut.mutate(dto)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-[var(--bd)]">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-[var(--blue)]" />
            <h3 className="text-base font-semibold text-[var(--brand-dark)]">
              {isEdit ? 'Modifier le composant' : 'Nouveau composant salaire'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-2)] rounded">
            <X className="w-5 h-5 text-[var(--tx3)]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Type</label>
            <select
              value={type}
              onChange={e => handleTypeChange(e.target.value as SalaryComponentType)}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm disabled:bg-[var(--surface-2)]"
            >
              {Object.entries(TYPE_LABELS).map(([key, lbl]) => (
                <option key={key} value={key}>{lbl}</option>
              ))}
            </select>
            {!isEdit && (
              <p className="text-xs text-[var(--tx3)] mt-1">
                Les valeurs imposable/IPRES/CSS sont pré-remplies selon le type — modifiables ci-dessous.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="BASE"
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Ordre d'affichage</label>
              <input
                type="number"
                value={order}
                onChange={e => setOrder(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Libellé</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Montant mensuel (XOF)</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="200000"
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm font-semibold"
            />
          </div>

          <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-lg p-4 space-y-2">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-2">Cotisations & traitement</p>

            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx2)]">Imposable (IRPP)</span>
              <input type="checkbox" checked={isImposable} onChange={e => setIsImposable(e.target.checked)} className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx2)]">Soumis IPRES</span>
              <input type="checkbox" checked={isSubjectToIpres} onChange={e => setIsSubjectToIpres(e.target.checked)} className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx2)]">Soumis CSS</span>
              <input type="checkbox" checked={isSubjectToCss} onChange={e => setIsSubjectToCss(e.target.checked)} className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx2)]">Soumis CFCE</span>
              <input type="checkbox" checked={isSubjectToCfce} onChange={e => setIsSubjectToCfce(e.target.checked)} className="w-4 h-4" />
            </label>
            <label className="flex items-center justify-between text-sm">
              <span className="text-[var(--tx2)]">Sujet au prorata (jours travaillés)</span>
              <input type="checkbox" checked={subjectToProrata} onChange={e => setSubjectToProrata(e.target.checked)} className="w-4 h-4" />
            </label>
            <p className="text-xs text-[var(--tx3)] mt-2">
              💡 Le transport non imposable jusqu'à 26 000 FCFA. Les indemnités de fonction et logement sont imposables.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-[var(--tx2)] mb-1.5 block">Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-[var(--bd)] rounded-lg text-sm"
              placeholder="Ex : revue annuelle, prime exceptionnelle…"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-sm font-medium text-[var(--tx2)] rounded-lg"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={createMut.isPending || updateMut.isPending}
              className="flex-1 px-4 py-2 bg-[var(--blue)] hover:bg-[#2DAFD9] text-sm font-medium text-white rounded-lg disabled:opacity-50"
            >
              {createMut.isPending || updateMut.isPending ? 'Enregistrement...' : (isEdit ? 'Mettre à jour' : 'Ajouter')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
